import type { components } from "@majitap/sdk";
import { api, ok } from "./api";
import type { Tone } from "./labels";

type Schemas = components["schemas"];
export type StaffApiToken = Schemas["StaffApiToken"];
export type StaffWebhook = Schemas["StaffWebhookEndpoint"];
export type WebhookDelivery = Schemas["WebhookDelivery"];
export type DeadLetter = Schemas["DeadLetter"];
export type OwnerRef = Schemas["OwnerRef"];

export const TOKEN_STATUS: Record<StaffApiToken["status"], [label: string, tone: Tone]> = {
  active: ["Active", "success"],
  expired: ["Expired", "neutral"],
  revoked: ["Revoked", "danger"],
};

/** Who switched an endpoint off, as people say it. */
export const DISABLED_REASON: Record<NonNullable<StaffWebhook["disabled_reason"]>, string> = {
  owner: "Off: switched off by its owner",
  failing: "Off: failing for a day",
  gone: "Off: the address answered 410 Gone",
  admin: "Off: switched off by MajiTap staff",
};

export function webhookBadge(w: Pick<StaffWebhook, "status" | "consecutive_failures">): [string, Tone] {
  if (w.status === "disabled") return ["Off", "neutral"];
  return w.consecutive_failures > 0 ? ["Failing", "warning"] : ["On", "success"];
}

export const DELIVERY_STATUS: Record<WebhookDelivery["status"], [label: string, tone: Tone]> = {
  queued: ["Queued", "neutral"],
  sending: ["Sending", "info"],
  succeeded: ["Delivered", "success"],
  failed: ["Failed, retrying", "warning"],
  dead: ["Gave up", "danger"],
};

/** The owner's page in the console. */
export function ownerPage(owner: OwnerRef | null | undefined): string | null {
  if (!owner) return null;
  return owner.type === "business" ? `/businesses/${owner.id}` : `/partners/${owner.id}`;
}

/** "order.created on order 0193…" for a failed event. */
export const eventText = (e: Pick<DeadLetter, "event_type" | "aggregate_type">) =>
  `${e.event_type} (${e.aggregate_type.replace(/_/g, " ")})`;

const path = (id: string) => ({ params: { path: { id } } });

export const developerActions = {
  revokeToken: (id: string) => ok(api.POST("/v1/admin/api-tokens/{id}/revoke", path(id))),
  disableWebhook: (id: string) => ok(api.POST("/v1/admin/webhooks/{id}/disable", path(id))),
  retryEvent: (id: string) => ok(api.POST("/v1/admin/outbox/dead-letters/{id}/retry", path(id))),
};
